import {
  GameHandler,
  CardModalData,
  DeckBuilderCardFunctions,
} from "@/interfaces/GameHandler";
import { CardItem, RBItem } from "@/interfaces/CardItem";
import {
  getCardImage,
  getCardData,
  getAltArts,
  getAACardId,
  getAACardIdData,
  getAAFromCardId,
  cleanCardData,
} from "@/lib/games/riftbound/getCardDataLocal";
import { getLeaderImage } from "@/lib/games/riftbound/archetypeManager";
import type {
  DeckIdentity,
  DeckZone,
  DeckZoneInput,
} from "@/interfaces/DeckIdentity";
import { SrcType } from "@/models/alt-arts";
import { getTCGPlayerCardImage } from "@/lib/tcgplayer/tcgplayer_helper";
import getDefaultFilters from "@/components/DeckBuilder/game/riftbound/filters/getDefaultFilters";
import { filterCards as filterCardsRB } from "@/components/DeckBuilder/game/riftbound/filters/filterCards";
import { DeckCardsMap } from "@/lib/games/onePiece/DeckCommonCardsFilter";
import { sortCards as rbSortCards } from "@/lib/games/riftbound/deckbuilderSortCards";
import { CARDS_PER_PAGE } from "@/components/DeckBuilder/config";
import { getImgproxyUrl } from "@/lib/imgproxy";
import { gameAssetUrl } from "@/lib/cdn";
import { sortCards } from "@/lib/deckZones";

export class RBHandler implements GameHandler {
  getSetImage(set: string): string {
    // Use CDN directly without imgproxy for set squares
    return gameAssetUrl("riftbound", "set-squares", `${set}.webp`, "1.0.1");
  }

  getCardImage(cardId: string, size: string, src: SrcType | "" = ""): string {
    // size/src were previously dropped, so every card came back full-resolution.
    return getCardImage(cardId, size as any, src);
  }

  getTcgPlayerCardData(tcgplayerId: string): CardItem | undefined {
    // For now, return undefined as TCGPlayer integration for Riftbound is not implemented
    return undefined;
  }

  getCardData(cardId: string): CardItem | undefined {
    return getCardData(cardId);
  }

  getLeaderData(leaderId: string): DeckIdentity | undefined {
    const cardData = getCardData(leaderId);
    const curLeader: DeckIdentity = {
      id: leaderId,
      name: cardData?.name || "",
      color: cardData?.color || "",
      set: cardData?.set || "",
      image: getLeaderImage(leaderId),
    };
    return curLeader;
  }

  hasLeader(): boolean {
    return false;
  }

  sortDecklist(cardList: string, leaderId?: string): string {
    // card list is of the form 4xRB-001;4xRB-002;...4xRB-100
    // need to sort by card_type, energy_cost, power_cost, name
    // champion units with matching leader tag come first

    const cards = cardList.split(";");

    // combine duplicates like aa arts
    const cardMap = new Map<string, number>();
    for (const card of cards) {
      const [count, cardId] = card.split("x");
      cardMap.set(cardId, (cardMap.get(cardId) || 0) + parseInt(count));
    }

    // Convert cardMap to array of [cardId, count] pairs for sorting
    const cardArray = Array.from(cardMap.entries()).map(([cardId, count]) => {
      const cardData = getCardData(cardId);
      return {
        cardId,
        count,
        cardData: cardData as RBItem | undefined,
      };
    });

    const leaderData = getCardData(leaderId || "");

    // Sort the array based on the specified criteria
    cardArray.sort((a, b) => {
      if (!a.cardData || !b.cardData) return 0;

      // Check if cards are champion units with matching leader tag
      const aIsChampionWithLeader =
        a.cardData.card_type === "Champion Unit" &&
        leaderData &&
        a.cardData.tags &&
        a.cardData.tags.includes(leaderData.tags[0]);
      const bIsChampionWithLeader =
        b.cardData.card_type === "Champion Unit" &&
        leaderData &&
        b.cardData.tags &&
        b.cardData.tags.includes(leaderData.tags[0]);

      // Champion units with matching leader tag come first
      if (aIsChampionWithLeader && !bIsChampionWithLeader) return -1;
      if (!aIsChampionWithLeader && bIsChampionWithLeader) return 1;

      // Sort by card_type first (treat champion units as units if no leader match)
      const typeOrder = [
        "Unit",
        "Spell",
        "Gear",
        "Rune",
        "Legend",
        "Token Unit",
        "Battlefield",
      ];

      // For sorting purposes, treat champion units as units if they don't match the leader
      // Also treat signature spells like spells and signature units like units
      const aType = aIsChampionWithLeader
        ? "Champion Unit"
        : a.cardData.card_type === "Champion Unit"
          ? "Unit"
          : a.cardData.card_type === "Signature Unit"
            ? "Unit"
            : a.cardData.card_type === "Signature Spell"
              ? "Spell"
              : a.cardData.card_type;
      const bType = bIsChampionWithLeader
        ? "Champion Unit"
        : b.cardData.card_type === "Champion Unit"
          ? "Unit"
          : b.cardData.card_type === "Signature Unit"
            ? "Unit"
            : b.cardData.card_type === "Signature Spell"
              ? "Spell"
              : b.cardData.card_type;

      const aTypeIndex = typeOrder.indexOf(aType);
      const bTypeIndex = typeOrder.indexOf(bType);
      if (aTypeIndex !== bTypeIndex) {
        return aTypeIndex - bTypeIndex;
      }

      // Sort by energy_cost
      const aEnergyCost = a.cardData.energy_cost || 0;
      const bEnergyCost = b.cardData.energy_cost || 0;
      if (aEnergyCost !== bEnergyCost) {
        return aEnergyCost - bEnergyCost;
      }

      // Sort by power_cost
      const aPowerCost = a.cardData.power_cost || 0;
      const bPowerCost = b.cardData.power_cost || 0;
      if (aPowerCost !== bPowerCost) {
        return aPowerCost - bPowerCost;
      }

      // Sort by name
      return a.cardData.name.localeCompare(b.cardData.name);
    });

    // Convert back to the original format
    return cardArray.map(({ cardId, count }) => `${count}x${cardId}`).join(";");
  }

  getSet(card: CardItem): string {
    return (card as RBItem).set || "";
  }

  getTraits(card: CardItem): string[] {
    return (card as RBItem).tags ?? [];
  }

  getCardModalData(): CardModalData {
    return {
      getCardImage: (cardId: string, tcgplayerId?: string | null) => {
        if (tcgplayerId) {
          return getTCGPlayerCardImage(tcgplayerId);
        }
        return getCardImage(cardId);
      },
      getCardType: (card: CardItem) => {
        return (card as RBItem).card_type;
      },
      getCardEffect: (card: CardItem) => {
        return [(card as RBItem).effect];
      },
      getCardTrigger: (card: CardItem) => {
        return []; // Riftbound doesn't have triggers like other games
      },
      getCardLink: (card: CardItem, tcgplayerId?: string | null) => {
        return tcgplayerId
          ? `https://www.tcgplayer.com/product/${tcgplayerId}`
          : "#";
      },
      getAdditionalChips: (card: CardItem) => {
        const rbCard = card as RBItem;
        const chips: string[] = [];
        // Riftbound doesn't have super property in current data
        return chips;
      },
    };
  }

  // DeckBuilder-specific methods
  getDeckBuilderCardFunctions(): DeckBuilderCardFunctions {
    return {
      getCardData,
      getAltArts,
      getAACardId,
      getAACardIdData,
      getAAFromCardId,
      cleanCardData,
    };
  }

  getDefaultFilters() {
    return getDefaultFilters();
  }

  getCardsPerPage(): number {
    return CARDS_PER_PAGE;
  }

  getModalCardData(aaCardId: string): CardItem | undefined {
    return aaCardId
      ? aaCardId.includes("_")
        ? getAACardIdData(aaCardId)
        : getCardData(aaCardId)
      : undefined;
  }

  filterCards(
    cards: CardItem[],
    filters: any,
    defaultFilters: any,
    deckCardsMap: DeckCardsMap,
  ): CardItem[] {
    return filterCardsRB(
      cards as RBItem[],
      filters,
      defaultFilters,
      deckCardsMap,
    );
  }

  // Optional game-specific methods
  getCost(card: CardItem): number {
    return (card as RBItem).cost || 0;
  }

  getEnergyCost(card: CardItem): number {
    return (card as RBItem).energy_cost || 0;
  }

  getPowerCost(card: CardItem): number {
    return (card as RBItem).power_cost || 0;
  }

  getColor(card: CardItem): string {
    return (card as RBItem).color;
  }

  isLeader(card: CardItem): boolean {
    return (card as RBItem).card_type === "Legend";
  }
  /**
   * Riftbound decks render as three zones: a setup zone holding the Legend,
   * Battlefields and Runes, then the main deck, then any sideboard.
   */
  getDeckZones(deck: DeckZoneInput): DeckZone[] {
    const list = deck.decklist ?? "";
    const setup = [
      `1x${deck.identityId}`,
      ...getCardsByCardType(list, "battlefield").split(";").filter(Boolean),
      ...getCardsByCardType(list, "rune").split(";").filter(Boolean),
    ].join(";");

    const zones: DeckZone[] = [
      { id: "setup", label: "Setup", cards: setup },
      {
        id: "main",
        label: "Main Deck",
        cards: sortCards(this, filterMainDeck(list), deck.identityId),
      },
    ];

    if (deck.sideboard) {
      zones.push({
        id: "sideboard",
        label: "Sideboard",
        cards: sortCards(this, deck.sideboard, deck.identityId),
      });
    }

    return zones;
  }

  isRotatable(card: CardItem): boolean {
    return (card as RBItem)?.card_type === "Battlefield";
  }

  sortCards(a: CardItem, b: CardItem): number {
    return rbSortCards(a as RBItem, b as RBItem);
  }
}

export function getCardsByCardType(cardList: string, cardType: string): string {
  const cards = cardList.split(";");
  const cardMap = new Map<string, number>();
  for (const card of cards) {
    const [count, cardId] = card.split("x");
    cardMap.set(cardId, (cardMap.get(cardId) || 0) + parseInt(count));
  }

  const cardArray = Array.from(cardMap.entries())
    .map(([cardId, count]) => {
      const cardData = getCardData(cardId);
      return {
        cardId,
        count,
        cardData: cardData as RBItem | undefined,
      };
    })
    .filter(
      ({ cardData }) =>
        cardData && cardData.card_type.toLowerCase() === cardType.toLowerCase(),
    );

  return cardArray
    .sort((a, b) => a.cardId.localeCompare(b.cardId))
    .map(({ cardId, count }) => `${count}x${cardId}`)
    .join(";");
}

export function filterMainDeck(cardList: string): string {
  const cards = cardList.split(";");
  const cardMap = new Map<string, number>();
  for (const card of cards) {
    const [count, cardId] = card.split("x");
    cardMap.set(cardId, (cardMap.get(cardId) || 0) + parseInt(count));
  }

  const cardArray = Array.from(cardMap.entries())
    .map(([cardId, count]) => {
      const cardData = getCardData(cardId);
      return {
        cardId,
        count,
        cardData: cardData as RBItem | undefined,
      };
    })
    // Exclude only what is *known* to belong to the setup zone. A card missing
    // from card_data.json (a new release, say) previously vanished from the
    // rendered deck entirely; showing it in the main deck is far better than
    // silently dropping it.
    .filter(
      ({ cardData }) =>
        !cardData ||
        (cardData.card_type !== "Rune" && cardData.card_type !== "Battlefield"),
    );

  return cardArray
    .sort((a, b) => a.cardId.localeCompare(b.cardId))
    .map(({ cardId, count }) => `${count}x${cardId}`)
    .join(";");
}
